// Formulário de sub-usuário — CRIAR (popup "Criar usuário" do Perfil, PWA)
// e EDITAR (mesmo popup, gerenciando quem já foi criado; e a aba USUÁRIOS
// em Editar Usuário, web) compartilham este único componente (pedido
// 2026-09-10/11). O titular escolhe quais dos SEUS veículos essa pessoa
// vai enxergar e um conjunto restrito e seguro de permissões (nunca
// administração, financeiro, documentos, contrato ou gestão de outros
// usuários — ver SUBUSER_PERMISSION_WHITELIST no backend, reforçado aqui
// só como UX, nunca como única linha de defesa).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import type { Vehicle } from "@/types";
import { useCreateSubUser, useSubUserDetail, useUpdateSubUser } from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { LicensePlate } from "@/components/license-plate";

// Mesmo critério de onlyDigits em routes/users.tsx (pedido 2026-09-01: só
// números, sem type="email" no vizinho autocompletando aqui por engano) —
// duplicado localmente em vez de importar de um módulo de rota.
function onlyDigits(value: string, maxLen = 15): string {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

// Cada toggle da UI é uma combinação amigável de 1+ categoria/ação do
// catálogo restrito do backend — nunca expõe os nomes técnicos
// (category/action) pro cliente, só o efeito prático.
const PERMISSION_TOGGLES: {
  key: string;
  label: string;
  description: string;
  grants: { category: string; actions: string[] }[];
}[] = [
  {
    key: "ver",
    label: "Ver localização e status dos veículos",
    description: "Enxerga no mapa onde o veículo está e se está ligado/desligado.",
    grants: [{ category: "devices", actions: ["view"] }],
  },
  {
    key: "bloquear",
    label: "Bloquear e desbloquear veículo",
    description: "Pode enviar o comando de bloqueio/desbloqueio remoto.",
    grants: [{ category: "devices", actions: ["lock", "unlock"] }],
  },
  {
    key: "notificacoes",
    label: "Receber notificações",
    description: "Recebe os mesmos alertas configurados pra esse veículo.",
    grants: [{ category: "notifications", actions: ["view"] }],
  },
  {
    key: "historico",
    label: "Ver histórico de rotas e viagens",
    description: "Consulta rotas, paradas, viagens e eventos passados.",
    grants: [{ category: "reports", actions: ["routes", "stops", "trips", "events"] }],
  },
  {
    // Pedido 2026-10-15: desligado, a pessoa nem vê "Apagar histórico" no
    // Perfil (e o backend recusa com 403).
    key: "apagar_historico",
    label: "Apagar histórico",
    description: "Pode apagar o histórico de rotas dos veículos (confirmando com a própria senha).",
    grants: [{ category: "reports", actions: ["delete_history"] }],
  },
];

function togglesFromPermissions(permissions: { category: string; actions: string[] }[]): Set<string> {
  const enabled = new Set<string>();
  for (const toggle of PERMISSION_TOGGLES) {
    const satisfied = toggle.grants.every((grant) => {
      const found = permissions.find((p) => p.category === grant.category);
      if (!found) return false;
      return grant.actions.every((a) => found.actions.includes(a));
    });
    if (satisfied) enabled.add(toggle.key);
  }
  return enabled;
}

// Universo de veículos pra marcar/desmarcar, já normalizado (id sempre
// number) — origem difere por modo: no CRIAR são os veículos do próprio
// titular logado (useVehicles(), id string); no EDITAR vêm prontos do
// backend (availableDevices, id number, sempre os do CRIADOR ORIGINAL,
// nunca de quem está editando).
interface PickableVehicle {
  id: number;
  name: string;
  plate?: string;
}

export function SubUserFormDialog({
  mode,
  open,
  onOpenChange,
  vehicles,
  subUserId,
}: {
  mode: "create" | "edit";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Obrigatório em mode="create" — veículos do próprio titular logado.
  vehicles?: Vehicle[];
  // Obrigatório em mode="edit".
  subUserId?: string;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Set<number>>(new Set());
  const [enabledToggles, setEnabledToggles] = useState<Set<string>>(new Set(["ver"]));
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateSubUser();
  const updateMutation = useUpdateSubUser();
  const saving = createMutation.isPending || updateMutation.isPending;

  const { data: detail, isLoading: loadingDetail } = useSubUserDetail(
    mode === "edit" && open ? subUserId : undefined,
  );

  const pickableVehicles: PickableVehicle[] =
    mode === "create"
      ? (vehicles ?? []).map((v) => ({ id: Number(v.id), name: v.name, plate: v.plate }))
      : (detail?.availableDevices ?? []).map((d) => ({
          id: d.id,
          name: d.name,
          plate: typeof d.attributes?.plate === "string" ? d.attributes.plate : undefined,
        }));

  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setName("");
      setEmail("");
      setPhone("");
      setPassword("");
      setConfirmPassword("");
      setSelectedVehicleIds(new Set());
      setEnabledToggles(new Set(["ver"]));
      setError(null);
    }
  }, [open, mode]);

  // Edição: pré-preenche assim que o detalhe chega (não roda de novo a
  // cada re-render — só quando o próprio `detail` muda de fato).
  useEffect(() => {
    if (mode !== "edit" || !detail) return;
    setName(detail.name);
    setEmail(detail.email);
    setPhone(detail.phone ?? "");
    setPassword("");
    setConfirmPassword("");
    setSelectedVehicleIds(new Set(detail.deviceIds));
    setEnabledToggles(togglesFromPermissions(detail.permissions));
    setError(null);
  }, [mode, detail]);

  const toggleVehicle = (id: number) => {
    setSelectedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTogglePermission = (key: string, checked: boolean) => {
    setEnabledToggles((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) {
      setError("Preencha nome e usuário/e-mail.");
      return;
    }
    if (!phone.trim()) {
      setError("Preencha o telefone.");
      return;
    }
    const changingPassword = mode === "create" || password.length > 0;
    if (changingPassword) {
      if (password.length < 6) {
        setError("A senha precisa ter pelo menos 6 caracteres.");
        return;
      }
      if (password !== confirmPassword) {
        setError("As senhas não coincidem.");
        return;
      }
    }
    if (mode === "create" && selectedVehicleIds.size === 0) {
      setError("Selecione ao menos um veículo.");
      return;
    }
    setError(null);

    // Junta os grants de cada toggle ligado numa lista {category, actions}
    // sem categoria duplicada (bloquear+ver, por ex., compartilham
    // "devices" — o backend também tolera categorias repetidas? não,
    // rejeita — então mescla aqui).
    const byCategory = new Map<string, Set<string>>();
    for (const key of enabledToggles) {
      const toggle = PERMISSION_TOGGLES.find((t) => t.key === key);
      if (!toggle) continue;
      for (const grant of toggle.grants) {
        const set = byCategory.get(grant.category) ?? new Set<string>();
        for (const action of grant.actions) set.add(action);
        byCategory.set(grant.category, set);
      }
    }
    const permissions = [...byCategory.entries()].map(([category, actions]) => ({
      category,
      actions: [...actions],
    }));
    const deviceIds = [...selectedVehicleIds];

    try {
      if (mode === "create") {
        const result = await createMutation.mutateAsync({
          name: name.trim(),
          email: email.trim(),
          phone,
          password,
          deviceIds,
          permissions,
        });
        toast.success(`Usuário "${result.name}" criado.`, {
          description: "A pessoa já pode entrar com o e-mail/usuário e senha que você definiu.",
        });
      } else {
        await updateMutation.mutateAsync({
          id: subUserId!,
          input: {
            name: name.trim(),
            email: email.trim(),
            phone,
            ...(changingPassword ? { password } : {}),
            deviceIds,
            permissions,
          },
        });
        toast.success("Usuário atualizado.");
      }
      onOpenChange(false);
    } catch (err) {
      if (err instanceof GpsDriveApiError) {
        setError(err.message);
      } else {
        setError("Não foi possível salvar agora. Tente de novo em alguns instantes.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-sky-600" />
            {mode === "create" ? "Criar usuário" : "Editar usuário"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Crie um acesso pra um parente ou outra pessoa acompanhar seus veículos. Você escolhe o que essa pessoa vai poder ver e fazer."
              : "Altere os dados, os veículos ou o que essa pessoa pode fazer. Deixe a senha em branco pra mantê-la como está."}
          </DialogDescription>
        </DialogHeader>

        {mode === "edit" && loadingDetail ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Carregando…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sub-user-name">Nome</Label>
              <Input id="sub-user-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da pessoa" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sub-user-email">E-mail ou usuário</Label>
              <Input
                id="sub-user-email"
                autoCapitalize="none"
                autoCorrect="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sub-user-phone">Telefone</Label>
              <Input
                id="sub-user-phone"
                type="tel"
                autoComplete="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(onlyDigits(e.target.value))}
                placeholder="DDD + número"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sub-user-password">{mode === "create" ? "Senha" : "Nova senha"}</Label>
              <Input
                id="sub-user-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "create" ? "Mínimo 6 caracteres" : "Deixe em branco pra manter a atual"}
              />
            </div>

            {(mode === "create" || password.length > 0) && (
              <div className="space-y-1.5">
                <Label htmlFor="sub-user-password-confirm">Confirmar senha</Label>
                <Input
                  id="sub-user-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Veículos que essa pessoa vai ver</Label>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-input p-2">
                {pickableVehicles.length === 0 ? (
                  <p className="p-2 text-xs text-muted-foreground">Nenhum veículo disponível.</p>
                ) : (
                  pickableVehicles.map((v) => (
                    <label
                      key={v.id}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <Checkbox
                        checked={selectedVehicleIds.has(v.id)}
                        onCheckedChange={() => toggleVehicle(v.id)}
                      />
                      <span className="truncate">
                        {v.name}
                        {v.plate && <LicensePlate plate={v.plate} size="xs" className="ml-1.5" />}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label>O que essa pessoa pode fazer</Label>
              {PERMISSION_TOGGLES.map((toggle) => (
                <div key={toggle.key} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{toggle.label}</div>
                    <div className="text-xs text-muted-foreground">{toggle.description}</div>
                  </div>
                  <Switch
                    checked={enabledToggles.has(toggle.key)}
                    onCheckedChange={(checked) => toggleTogglePermission(toggle.key, checked)}
                  />
                </div>
              ))}
            </div>

            {error && <p className="text-sm font-medium text-red-600">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving || (mode === "edit" && loadingDetail)}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvando…
              </>
            ) : mode === "create" ? (
              "Criar usuário"
            ) : (
              "Salvar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
